import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { createAppKit } from '@reown/appkit'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { watchAccount, watchChainId, getAccount, getChainId, disconnect, Config, reconnect } from '@wagmi/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment.development';
import { AccountChangeEvent, ChainChangeEvent, WalletState } from '../interfaces/common-interface';
import { LocalStorage } from './local-storage.service';

export const networks = (environment.SUPPORTED_NETWORKS ?? [environment.DEFAULT_NETWORK]) as [any, ...any[]];

@Injectable({
  providedIn: 'root',
})
export class AppKitService {

  private modal: any;
  private wagmiConfig!: Config;
  private initialized = false;
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private wagmiAdapter: WagmiAdapter | null = null; // Add this line
  private readonly localStorageService = inject(LocalStorage);

  // Angular v19.2 Signals
  walletState = signal<WalletState>({
    isConnected: false,
    address: null,
    chainId: null,
    networkName: null,
    isCorrectNetwork: false
  });

  // Computed signals
  isConnected = computed(() => this.walletState().isConnected);
  currentAddress = computed(() => this.walletState().address);
  currentChainId = computed(() => this.walletState().chainId);
  isCorrectNetwork = computed(() => this.walletState().isCorrectNetwork);

  // Event subjects for specific change events
  private accountChange$ = new Subject<AccountChangeEvent>();
  private chainChange$ = new Subject<ChainChangeEvent>();
  private connectionStatus$ = new BehaviorSubject<boolean>(false);
  previousAddress: any;

  constructor() {
    this.initializeAppkit();
  }

  async initializeAppkit() {
    console.log('in appkit')
    if (this.initialized) return;
    try {
      // 1. Get a project ID at https://dashboard.reown.com
      const projectId = environment.APPKIT_PROJECT_ID;

      // 2. Set up Wagmi adapter
      this.wagmiAdapter = new WagmiAdapter({
        projectId,
        networks
      });

      console.log('Wagmi adapter created:', this.wagmiAdapter);

      // 3. Extract wagmi config - THIS IS THE KEY FIX
      this.wagmiConfig = this.wagmiAdapter?.wagmiConfig;

      console.log('Wagmi config extracted:', this.wagmiConfig);

      if (!this.wagmiConfig) {
        throw new Error('Failed to extract wagmi config from adapter');
      }

      // 3. Configure the metadata
      const metadata = {
        name: 'AOC Multisig',
        description: 'AOC Multisig App with Reown AppKit',
        url: environment.APP_URL, // origin must match your domain & subdomain
        icons: ['https://avatars.githubusercontent.com/u/179229932']
      }

      // 3. Create the modal
      this.modal = createAppKit({
        adapters: [this.wagmiAdapter],
        networks: networks as any,
        metadata,
        projectId,
        features: {
          analytics: true, // Optional - defaults to your Cloud configuration
          socials: false,
          email: false
        }
      })
      this.initialized = true;
      // Set up watchers after initialization
      await this.setupWatchers();

      // Initialize current state
      this.updateCurrentState();

      console.log('AppKit initialized successfully');

      // // 4. Trigger modal programaticaly
      // const openConnectModalBtn = document.getElementById('open-connect-modal')
      // // const openNetworkModalBtn = document.getElementById('open-network-modal')

      // openConnectModalBtn?.addEventListener('click', () => modal.open())
      // // openNetworkModalBtn?.addEventListener('click', () => modal.open({ view: 'Networks' }))

    } catch (error) {
      console.error('Failed to initialize AppKit:', error);
      throw error;
    }
  }

  /**
   * Set up account and chain watchers using Angular v19.2 patterns
   */
  private async setupWatchers(): Promise<void> {
    if (!this.wagmiConfig) {
      console.error('Cannot setup watchers: wagmiConfig is undefined');
      throw new Error('wagmiConfig is not available');
    }

    console.log('Setting up watchers with config:', this.wagmiConfig);

    try {
      // Watch for account changes
      const accountUnsubscribe = watchAccount(this.wagmiConfig, {
        onChange: (account) => {
          console.log('Account changed:', account);
          const accountEvent: AccountChangeEvent = {
            address: account.address || null,
            isConnected: account.isConnected,
            connector: account.connector
          };

          // Emit account change event
          this.accountChange$.next(accountEvent);

          // Update wallet state
          this.updateWalletState({
            isConnected: account.isConnected,
            address: account.address || null
          });

          // Update connection status
          this.connectionStatus$.next(account.isConnected);
        }
      });

      // Watch for chain ID changes
      const chainUnsubscribe = watchChainId(this.wagmiConfig, {
        onChange: (chainId) => {
          console.log('Chain ID changed:', chainId);

          const networkInfo = this.getNetworkInfo(chainId);
          const chainEvent: ChainChangeEvent = {
            chainId,
            networkName: networkInfo.name,
            isSupported: networkInfo.isSupported
          };

          // Emit chain change event
          this.chainChange$.next(chainEvent);

          // Update wallet state
          this.updateWalletState({
            chainId,
            networkName: networkInfo.name,
            isCorrectNetwork: networkInfo.isSupported
          });
        }
      });

      // Subscribe to account changes with takeUntilDestroyed (Angular v19.2)
      this.accountChange$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(event => {
          this.onAccountChange(event);
        });

      // Subscribe to chain changes with takeUntilDestroyed (Angular v19.2)
      this.chainChange$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(event => {
          this.onChainChange(event);
        });

      console.log('Watchers set up successfully');

    } catch (error) {
      console.error('Error setting up watchers:', error);
      throw error;
    }
  }

  /**
   * Handle account change events
   */
  private async onAccountChange(event: AccountChangeEvent): Promise<void> {
    console.log('Processing account change:', event);

    const loginResponseStr = this.localStorageService.getDataByKey('loginResponse');
    if (loginResponseStr) {
      const loginResponse = JSON.parse(loginResponseStr);
      this.previousAddress = loginResponse.walletAddress;
    }

    // Only act if already connected and address changed
    if (
      event.isConnected &&
      event.address &&
      this.previousAddress &&
      event.address.toLowerCase() !== this.previousAddress.toLowerCase()
    ) {
      console.log('Address changed from previously connected address. Disconnecting and navigating to root...');
      await this.disconnectWallet();
      this.router.navigate(['/']);
      this.localStorageService.clearAllStorage();
      this.previousAddress = null; // reset
      return;
    }
    if (event.isConnected && event.address) {
      console.log(`Wallet connected: ${event.address}`);
      // Perform actions when wallet connects
      this.onWalletConnected(event.address);
    } else {
      console.log('Wallet disconnected');
      // Perform actions when wallet disconnects
      this.onWalletDisconnected();
    }
  }

  /**
   * Handle chain change events
   */
  private onChainChange(event: ChainChangeEvent): void {
    console.log('Processing chain change:', event);

    if (!event.isSupported) {
      console.warn(`Unsupported network: ${event.networkName} (${event.chainId})`);
      // Handle unsupported network
      this.onUnsupportedNetwork(event);
    } else {
      console.log(`Switched to supported network: ${event.networkName}`);
      // Handle supported network switch
      this.onSupportedNetworkSwitch(event);
    }
  }

  /**
   * Actions to perform when wallet connects
   */
  private onWalletConnected(address: string): void {
    // Add your custom logic here
    console.log('Wallet connected, performing initialization...');

    // Example: Load user data, initialize contracts, etc.
    // this.loadUserData(address);
    // this.initializeContracts();
  }

  /**
   * Actions to perform when wallet disconnects
   */
  private onWalletDisconnected(): void {
    // Add your custom logic here
    console.log('Wallet disconnected, cleaning up...');

    // Example: Clear user data, reset state, etc.
    // this.clearUserData();
    // this.resetApplicationState();
  }

  /**
   * Handle unsupported network
   */
  private onUnsupportedNetwork(event: ChainChangeEvent): void {
    console.warn('User switched to unsupported network');

    // Example: Show notification, prompt network switch, etc.
    // this.showNetworkSwitchPrompt();
  }

  /**
   * Handle supported network switch
   */
  private onSupportedNetworkSwitch(event: ChainChangeEvent): void {
    console.log('User switched to supported network');

    // Example: Refresh contracts, update UI, etc.
    // this.refreshContractsForNetwork(event.chainId);
  }

  /**
   * Update wallet state signal
   */
  private updateWalletState(partialState: Partial<WalletState>): void {
    this.walletState.update(current => ({
      ...current,
      ...partialState
    }));
  }

  /**
   * Update current state from wagmi
   */
  private updateCurrentState(): void {
    const account = getAccount(this.wagmiConfig);
    const chainId = getChainId(this.wagmiConfig);
    const networkInfo = this.getNetworkInfo(chainId);

    this.updateWalletState({
      isConnected: account.isConnected,
      address: account.address || null,
      chainId,
      networkName: networkInfo.name,
      isCorrectNetwork: networkInfo.isSupported
    });
  }

  /**
   * Get network information by chain ID
   */
  private getNetworkInfo(chainId: any): { name: string; isSupported: boolean } {
    const supportedNetworks = networks;
    const network = supportedNetworks.find(n => n.id === chainId);

    return {
      name: network?.name || `Unknown Network (${chainId})`,
      isSupported: !!network
    };
  }

  // Public Methods

  /**
   * Resolve chain configuration for a logical API network key (e.g. 'BSC', 'ETH', 'POLYGON')
   * using environment.SUPPORTED_NETWORKS, environment.RPC_URLS and environment.EXPLORER_URLS.
   */
  private getChainConfigForNetwork(networkKey: string):
    | { chainIdHex: string; chainName: string; rpcUrls: string[]; nativeCurrency: { name: string; symbol: string; decimals: number }; blockExplorerUrls?: string[] }
    | null {
    const upperKey = networkKey.toUpperCase();
    const supportedChains = (environment as any).SUPPORTED_NETWORKS ?? [environment.DEFAULT_NETWORK];

    // Find matching chain from SUPPORTED_NETWORKS based on name
    let targetChain: any | null = null;
    for (const chain of supportedChains) {
      const name = (chain as any).name?.toUpperCase() || '';
      if (
        (upperKey === 'BSC' && name.includes('BSC')) ||
        (upperKey === 'ETH' && (name.includes('SEPOLIA') || name.includes('HOLESKY') || name.includes('ETHEREUM'))) ||
        (upperKey === 'POLYGON' && (name.includes('POLYGON') || name.includes('AMOY')))
      ) {
        targetChain = chain;
        break;
      }
    }

    if (!targetChain) {
      return null;
    }

    const chainId: number = (targetChain as any).id;
    const chainIdHex = '0x' + chainId.toString(16);
    const chainName: string = (targetChain as any).name || upperKey;

    // RPC URL from env
    const rpcUrlsMap = (environment as any).RPC_URLS || {};
    const rpcUrl = rpcUrlsMap[chainId] || environment.RPC_URL;

    // Explorer URL from env.EXPLORER_URLS (strip trailing /tx if present)
    const explorerUrlsMap = (environment as any).EXPLORER_URLS || {};
    const explorerTxBase: string | undefined = explorerUrlsMap[upperKey];
    const blockExplorerUrl = explorerTxBase
      ? explorerTxBase.replace(/\/tx\/?$/, '')
      : undefined;

    // Native currency heuristic based on key
    let nativeCurrency = { name: 'ETH', symbol: 'ETH', decimals: 18 };
    if (upperKey === 'BSC') {
      nativeCurrency = { name: 'BNB', symbol: 'BNB', decimals: 18 };
    } else if (upperKey === 'POLYGON') {
      nativeCurrency = { name: 'MATIC', symbol: 'MATIC', decimals: 18 };
    }

    const cfg: {
      chainIdHex: string;
      chainName: string;
      rpcUrls: string[];
      nativeCurrency: { name: string; symbol: string; decimals: number };
      blockExplorerUrls?: string[];
    } = {
      chainIdHex,
      chainName,
      rpcUrls: [rpcUrl],
      nativeCurrency,
    };

    if (blockExplorerUrl) {
      cfg.blockExplorerUrls = [blockExplorerUrl];
    }

    return cfg;
  }

  /**
   * Open connect modal
   */
  async openConnectModal(): Promise<void> {
    if (!this.initialized) {
      throw new Error('AppKit not initialized');
    }
    await this.modal.open();
  }

  /**
   * Open network selection modal
   */
  async openNetworkModal(): Promise<void> {
    if (!this.initialized) {
      throw new Error('AppKit not initialized');
    }
    await this.modal.open({ view: 'Networks' });
  }

  /**
   * Ensure the wallet is on the correct network for a given API network key
   * (e.g. 'BSC', 'ETH', 'POLYGON'), using environment.SUPPORTED_NETWORKS,
   * environment.RPC_URLS and environment.EXPLORER_URLS for configuration.
   */
  async ensureNetworkForTransaction(apiNetwork: string | undefined): Promise<boolean> {
    if (!apiNetwork) return true;

    const networkKey = apiNetwork.toUpperCase();
    const cfg = this.getChainConfigForNetwork(networkKey);
    if (!cfg) {
      console.warn(`No matching chain found in SUPPORTED_NETWORKS for network key: ${networkKey}`);
      return true; // don't block, just proceed on current chain
    }

    const ethereum = (window as any).ethereum;
    if (!ethereum || typeof ethereum.request !== 'function') {
      console.warn('window.ethereum not available for network switching');
      return false;
    }

    try {
      // If MetaMask is already on the correct chain, nothing to do
      const currentChainIdHex = await ethereum.request({ method: 'eth_chainId' });
      if (currentChainIdHex?.toLowerCase() === cfg.chainIdHex.toLowerCase()) {
        return true;
      }
    } catch {
      // Ignore eth_chainId errors; we'll still attempt switch/add
    }

    try {
      // First try to switch to the chain if it's already added
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: cfg.chainIdHex }],
      });
      return true;
    } catch (switchError: any) {
      // If the chain is not added to MetaMask, try to add it
      if (switchError?.code === 4902) {
        try {
          await ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [cfg],
          });
          return true;
        } catch (addError) {
          console.error('Failed to add Ethereum chain', addError);
          return false;
        }
      }

      console.error('wallet_switchEthereumChain failed', switchError);
      return false;
    }
  }

  /**
   * Disconnect wallet
   */
  async disconnectWallet(): Promise<void> {
    try {
      // Ensure Wagmi config exists
      if (!this.wagmiConfig) {
        console.warn('wagmiConfig not initialized — initializing AppKit...');
        await this.initializeAppkit();
      }

      // Refresh connector if stale
      let account = getAccount(this.wagmiConfig);
      if (!account?.connector || typeof (account.connector as any).disconnect !== 'function') {
        console.warn('Connector missing or invalid — attempting to reconnect');
        await reconnect(this.wagmiConfig); // rebuild connector instance
        account = getAccount(this.wagmiConfig);
      }

      // Attempt disconnect safely
      const connector = account?.connector as any;
      if (connector && typeof connector.disconnect === 'function') {
        await connector.disconnect();
        console.log('Disconnected via connector.disconnect()');
      } else {
        await disconnect(this.wagmiConfig);
        console.log('Disconnected via wagmi.disconnect() fallback');
      }

      // Clear Wagmi internal caches
      this.localStorageService.clearLocalStorage('wagmi.connected');
      this.localStorageService.clearLocalStorage('wagmi.wallet');
      this.localStorageService.clearLocalStorage('walletconnect');
      this.localStorageService.clearLocalStorage('wc@2:client');

      // Reset app wallet state
      this.walletState.set({
        isConnected: false,
        address: null,
        chainId: null,
        networkName: null,
        isCorrectNetwork: false,
      });
      this.connectionStatus$.next(false);
      console.log('Wallet state reset successfully');
    } catch (error) {
      console.error('Failed to disconnect wallet:', error);

      // Even if disconnect fails, ensure app state is cleaned
      this.localStorageService.clearLocalStorage('wagmi.connected');
      this.localStorageService.clearLocalStorage('wagmi.wallet');
      this.localStorageService.clearLocalStorage('walletconnect');
      this.localStorageService.clearLocalStorage('wc@2:client');
      this.walletState.set({
        isConnected: false,
        address: null,
        chainId: null,
        networkName: null,
        isCorrectNetwork: false,
      });
    }
  }



  /**
   * Check if wallet is connected
   */
  isWalletConnected(): boolean {
    return this.isConnected();
  }

  /**
   * Get current wallet address
   */
  getCurrentAddress(): string | null {
    return this.currentAddress();
  }

  /**
   * Get current chain ID
   */
  getCurrentChainId(): number | null {
    return this.currentChainId();
  }

  /**
   * Check if current network is supported
   */
  isCurrentNetworkSupported(): boolean {
    return this.isCorrectNetwork();
  }

  // Observable getters for components that need to subscribe

  /**
   * Get account change observable
   */
  getAccountChanges(): Observable<AccountChangeEvent> {
    return this.accountChange$.asObservable();
  }

  /**
   * Get chain change observable
   */
  getChainChanges(): Observable<ChainChangeEvent> {
    return this.chainChange$.asObservable();
  }

  /**
   * Get connection status observable
   */
  getConnectionStatus(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Get wallet state as observable (for components not using signals)
   */
  getWalletStateObservable(): Observable<WalletState> {
    // Convert signal to observable if needed
    return new Observable(subscriber => {
      const unsubscribe = () => { }; // Signal cleanup handled by Angular
      subscriber.next(this.walletState());
      return unsubscribe;
    });
  }

  /**
   * Manual trigger for modal (keeping backward compatibility)
   */
  setupModalTriggers(): void {
    const openConnectModalBtn = document.getElementById('open-connect-modal');
    const openNetworkModalBtn = document.getElementById('open-network-modal');

    openConnectModalBtn?.addEventListener('click', () => this.openConnectModal());
    openNetworkModalBtn?.addEventListener('click', () => this.openNetworkModal());
  }
}
